import { useCallback, useEffect, useState } from "react";
import { Search, Gift, X, Crown } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { fmtDateTime } from "./KotHistory";
import { CustomerLedger } from "./CustomerLedger";

function History({ phone, branch, onClose }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("history");
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/customers/${encodeURIComponent(phone)}/history`).then((r) => setD(r.data)).catch((e) => setErr(errorMessage(e))); }, [phone]);
  return (
    <div className="modal-backdrop"><div className="modal wide" data-testid="customer-history-modal">
      <div className="modal-head"><div><span className="overline">CUSTOMER</span><h2>{d?.customer.name || phone}</h2></div><button className="icon-button" onClick={onClose} data-testid="customer-history-close"><X size={18} /></button></div>
      <div className="filters">
        <button className={tab === "history" ? "primary-button" : "secondary-button"} onClick={() => setTab("history")} data-testid="customer-tab-history">Purchase history</button>
        <button className={tab === "ledger" ? "primary-button" : "secondary-button"} onClick={() => setTab("ledger")} data-testid="customer-tab-ledger">Ledger &amp; dues</button>
      </div>
      {err && <div className="auth-alert error">{err}</div>}
      {tab === "ledger" && <CustomerLedger phone={phone} branch={branch} />}
      {tab === "history" && d && (
        <>
          <div className="ins-totals" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
            <div><span>Visits</span><b>{d.customer.visits}</b></div>
            <div><span>Total spent</span><b>{money(d.customer.total_spent)}</b></div>
            <div><span>Points balance</span><b data-testid="customer-history-points">{(d.customer.points || 0).toFixed(2)}</b></div>
            <div><span>Earned / redeemed</span><b>{(d.customer.points_earned || 0).toFixed(0)} / {(d.customer.points_redeemed || 0).toFixed(0)}</b></div>
          </div>
          <div className="table-wrap"><table data-testid="customer-history-table">
            <thead><tr><th>Date (IST)</th><th>Invoice</th><th>Where</th><th>Items</th><th>Total</th><th>Points</th></tr></thead>
            <tbody>{d.sales.map((s) => (
              <tr key={s.id}><td>{fmtDateTime(s.created_at)}</td><td><a href={`/invoice/${s.id}`} target="_blank" rel="noreferrer">{s.invoice_no}</a></td><td>{s.outlet?.display_name} · {s.table_label || s.channel}</td><td>{s.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</td><td>{money(s.grand_total)}</td><td>{s.loyalty ? `+${s.loyalty.earned}${s.loyalty.redeemed ? ` / −${s.loyalty.redeemed}` : ""}` : "—"}</td></tr>
            ))}</tbody>
          </table></div>
        </>
      )}
    </div></div>
  );
}

export default function CustomersView({ branch }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("visits");
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/customers?q=${encodeURIComponent(q)}&sort=${sort}`).then((r) => { setRows(r.data); setErr(""); }).catch((e) => setErr(errorMessage(e))), [q, sort]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const top = [...rows].sort((a, b) => b.visits - a.visits || b.total_spent - a.total_spent).slice(0, 3);
  return (
    <div data-testid="customers-page">
      {top.length > 0 && (
        <div className="ins-highlights" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
          {top.map((c, i) => (
            <div className={`ins-card ${i === 0 ? "gold" : ""}`} key={c.phone} data-testid={`top-customer-${i}`}>
              <span className="ins-ico">{i === 0 ? <Crown size={16} /> : <Gift size={16} />}</span>
              <span className="overline">#{i + 1} RETURNING CUSTOMER</span><strong>{c.name}</strong>
              <small>{c.visits} visits · {money(c.total_spent)} · {(c.points || 0).toFixed(2)} pts</small>
            </div>
          ))}
        </div>
      )}
      <div className="panel full-panel">
        <div className="filters kot-filters">
          <div className="search"><Search size={14} /><input placeholder="Search name or mobile" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search" /></div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="customers-sort">
            <option value="visits">Most visits</option><option value="spent">Highest spend</option><option value="points">Most points</option><option value="recent">Recent</option>
          </select>
          <span className="filter-spacer" /><span className="muted-label" data-testid="customers-count">{rows.length} customers</span>
        </div>
        {err && <div className="auth-alert error" style={{ margin: 16 }}>{err}</div>}
        <div className="table-wrap"><table data-testid="customers-table">
          <thead><tr><th>Customer</th><th>Mobile</th><th>Visits</th><th>Total spent</th><th>Points (₹)</th><th>Last visit</th></tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={6}><div className="empty small">No customers yet. They're added automatically when a mobile number is entered at billing.</div></td></tr>}
            {rows.map((c) => (
              <tr key={c.phone} className="clickable" onClick={() => setOpen(c.phone)} data-testid={`customer-row-${c.phone.replace("+", "")}`}>
                <td><b>{c.name}</b></td><td>{c.phone}</td><td>{c.visits}</td><td>{money(c.total_spent)}</td><td>{(c.points || 0).toFixed(2)}</td><td>{c.last_visit ? fmtDateTime(c.last_visit) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
      {open && <History phone={open} branch={branch} onClose={() => setOpen(null)} />}
    </div>
  );
}
